import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { api } from '@/lib/api'
import type { AuditLogEntry, Page, RevenueCatEvent } from '@/lib/types'
import { PageHeader } from '@/components/AppLayout'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { formatDate } from '@/lib/utils'
import { ErrorNote } from './DashboardPage'
import { Pagination } from './UsersPage'

/**
 * The audit trail and the webhook feed.
 *
 * Every privileged action writes an audit row, and the actor comes from the
 * verified token rather than the request body, so the trail cannot be forged by
 * whoever performed the action.
 */
export function AuditPage() {
  const [tab, setTab] = useState<'audit' | 'webhooks'>('audit')
  const [page, setPage] = useState(0)

  const audit = useQuery({
    queryKey: ['audit', page],
    queryFn: () => api.get<Page<AuditLogEntry>>('api/admin/audit', { page, size: 50 }),
    enabled: tab === 'audit',
  })

  const webhooks = useQuery({
    queryKey: ['webhooks', page],
    queryFn: () => api.get<Page<RevenueCatEvent>>('api/admin/revenuecat/events', { page, size: 50 }),
    enabled: tab === 'webhooks',
  })

  const active = tab === 'audit' ? audit : webhooks

  return (
    <>
      <PageHeader title="Audit log" description="Administrative actions and RevenueCat webhook deliveries." />

      <div className="space-y-4 p-8">
        {active.error && <ErrorNote message={(active.error as Error).message} />}

        <div className="flex gap-2">
          <Button
            variant={tab === 'audit' ? 'default' : 'secondary'}
            size="sm"
            onClick={() => { setTab('audit'); setPage(0) }}
          >
            Admin actions
          </Button>
          <Button
            variant={tab === 'webhooks' ? 'default' : 'secondary'}
            size="sm"
            onClick={() => { setTab('webhooks'); setPage(0) }}
          >
            Webhook events
          </Button>
        </div>

        <div className="rounded-[var(--radius-card)] border bg-[var(--color-surface)]">
          {tab === 'audit' ? (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>When</TableHead>
                  <TableHead>Actor</TableHead>
                  <TableHead>Action</TableHead>
                  <TableHead>Target</TableHead>
                  <TableHead>Detail</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {audit.data?.items.map((entry) => (
                  <TableRow key={entry.id}>
                    <TableCell className="whitespace-nowrap text-[var(--color-text-secondary)]">
                      {formatDate(entry.createdAt)}
                    </TableCell>
                    <TableCell className="text-[var(--color-text-secondary)]">
                      {entry.actorEmail ?? entry.actorSubject}
                    </TableCell>
                    <TableCell><Badge variant="outline">{entry.action}</Badge></TableCell>
                    <TableCell className="text-[var(--color-text-tertiary)]">
                      {entry.targetType ? `${entry.targetType} ${entry.targetId?.slice(0, 8) ?? ''}` : '—'}
                    </TableCell>
                    <TableCell className="max-w-xs truncate text-[var(--color-text-tertiary)]">
                      {entry.detail ?? '—'}
                    </TableCell>
                  </TableRow>
                ))}
                {audit.data?.items.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={5} className="text-center text-[var(--color-text-tertiary)]">
                      No administrative actions recorded yet.
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Received</TableHead>
                  <TableHead>Event</TableHead>
                  <TableHead>App user</TableHead>
                  <TableHead>Processed</TableHead>
                  <TableHead>Error</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {webhooks.data?.items.map((event) => (
                  <TableRow key={event.eventId}>
                    <TableCell className="whitespace-nowrap text-[var(--color-text-secondary)]">
                      {formatDate(event.receivedAt)}
                    </TableCell>
                    <TableCell><Badge variant="outline">{event.eventType}</Badge></TableCell>
                    <TableCell className="text-[var(--color-text-tertiary)]">
                      <code className="text-xs">{event.appUserId?.slice(0, 12) ?? '—'}</code>
                    </TableCell>
                    <TableCell>
                      <Badge variant={event.processed ? 'success' : 'danger'}>
                        {event.processed ? 'Yes' : 'No'}
                      </Badge>
                    </TableCell>
                    <TableCell className="max-w-xs truncate text-[var(--color-text-tertiary)]">
                      {event.errorDetail ?? '—'}
                    </TableCell>
                  </TableRow>
                ))}
                {webhooks.data?.items.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={5} className="text-center text-[var(--color-text-tertiary)]">
                      No webhook events received yet.
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          )}
        </div>

        <Pagination
          page={active.data?.page ?? 0}
          totalPages={active.data?.totalPages ?? 0}
          totalItems={active.data?.totalItems ?? 0}
          onChange={setPage}
        />
      </div>
    </>
  )
}
